import { trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AssetCommentAnnotationDto,
  AssetDto,
  CommentDto,
  DefaultDisplayGuestDto,
  DisplayUser,
  GalleryDto,
} from '@picflow/types';
import { Observable, Subject, map, take, takeUntil, tap } from 'rxjs';
import { fadeUpIf } from '../../../animations/fade-up.if.animation';
import { ResolvedCommentsPipe } from '../../../gallery/pipes/resolved-comments.pipe';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { sleep } from '../../../lib/sleep';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { AssetFeedbackService } from '../../../services/asset-feedback.service';
import { CommentService } from '../../../services/comment.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { UpdateService } from '../../../services/update.service';
import { ClickOutsideDirective } from '../../../shared-extra/directives/click-outside.directive';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { Shape, SimpleAnnotation } from '../../types/shapes';
import { CommentThreadComponent } from '../comment-thread/comment-thread.component';
import { NewCommentThreadComponent } from '../new-comment-thread/new-comment-thread.component';
import { VideoTimeData } from '../video-viewer/video-viewer.component';

export interface Comment extends CommentDto {
  id: string;
  created_at: number;
  authorId: string;
  user: DisplayUser;
  guest: DefaultDisplayGuestDto;
}

export interface CommentThread extends Comment {
  replies: Comment[];
  resolvedTempOverride?: boolean;
}

@Component({
  selector: 'app-comments',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './comments.component.html',
  styleUrl: './comments.component.scss',
  animations: [trigger('newCommentThread', fadeUpIf)],
  imports: [
    AsyncPipe,
    CommentThreadComponent,
    ClickOutsideDirective,
    ButtonDirective,
    ResolvedCommentsPipe,
    NewCommentThreadComponent,
    SvgComponent,
  ],
})
export class CommentsComponent implements OnChanges, OnInit, OnDestroy {
  public state = inject(StateService);
  private router = inject(Router);
  private commentService = inject(CommentService);
  private feedbackService = inject(AssetFeedbackService);
  private permissionService = inject(PermissionService);
  private updateService = inject(UpdateService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private route = inject(ActivatedRoute);
  private check = inject(ChangeDetectorRef);
  comments$: Observable<CommentThread[]>;
  resolvedCommentCount$: Observable<number>;
  unresolvedCommentCount$: Observable<number>;
  replyFocused = signal(false);
  destroyed$ = new Subject<void>();

  @ViewChild('threads') threads: ElementRef<HTMLDivElement>;
  @Input() asset: AssetDto;
  @Input() isGalleryGuest: boolean;
  @Input() annotationMode: boolean;
  @Input() isDesktop: boolean;
  @Input() videoTime: VideoTimeData;
  @Input() commentMessage: { text: string; addVideoFramePosition: boolean; private: boolean };
  @Input() commentPlacementMode: boolean;
  @Input() createAnnotationShapes: Shape[];
  @Input() disableClickOutside: boolean;
  @Input() hideNewCommentThread: boolean;
  @Input() hoverThreadId: string;
  @Input() showResolved: boolean;
  gallery = input<GalleryDto>();
  accessLevel = input<GalleryAccessLevel>();
  enabledAndAllowedFeedbackOptions = computed(() =>
    this.gallery() && this.accessLevel()
      ? this.permissionService.getAllowedAndEnabledFeedbackOptions(this.gallery(), this.accessLevel())
      : []
  );

  @Output() viewAnnotation = new EventEmitter<SimpleAnnotation>();
  @Output() videoTimeChange = new EventEmitter<number>();
  @Output() toggleAnnotationMode = new EventEmitter<void>();
  @Output() onNewThread = new EventEmitter<CommentThread>();
  @Output() selectedThreadChange = new EventEmitter<string>();
  @Output() placeComment = new EventEmitter<boolean>();
  @Output() threadHover = new EventEmitter<CommentThread>();
  @Output() zoomToComment = new EventEmitter<CommentThread>();
  @Output() toggleShowResolved = new EventEmitter<void>();

  selectedThread = signal<CommentThread>(undefined);

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe(params => {
      if (params.comment) {
        this.comments$.pipe(take(1)).subscribe(async threads => {
          const thread = threads.find(thread => thread.id === params.comment);
          if (thread) {
            await sleep(1);
            if (this.selectedThread()?.id === thread.id) return;
            this.selectThread(thread);
            this.router.navigate([], {
              relativeTo: this.route,
            });
          }
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.asset) {
      this.updateService.subscribeAsset(this.asset.id);
    }

    if (changes.asset && this.selectedThread()) {
      this.selectThread(undefined);
    }

    if (changes.asset) {
      this.comments$ = this.commentService.getComments(this.asset.id, this.gallery().id).pipe(
        map(comments =>
          this.commentService.filterCommentsForAccessLevel(comments, this.accessLevel(), this.isGalleryGuest)
        ),
        map((comments: Comment[]) => {
          comments.forEach(comment => {
            comment.authorId = (comment.user as DisplayUser)?.id || (comment.guest as DefaultDisplayGuestDto).id;
          });

          const threads = comments.filter(comment => !comment.parent_comment) as CommentThread[];
          threads.forEach(thread => {
            thread.replies = comments.filter(comment => comment.parent_comment === thread.id);
            thread.replies.sort((a, b) => a.created_at - b.created_at);
          });

          threads.sort((a, b) => {
            if (a.resolved && !b.resolved) return 1;
            if (!a.resolved && b.resolved) return -1;
            return b.created_at - a.created_at;
          });

          return threads;
        })
      );

      this.resolvedCommentCount$ = this.comments$.pipe(
        map(comments => comments.filter(comment => comment.resolved).length),
        tap(count => {
          if (!count && this.showResolved) {
            this.toggleShowResolved.emit();
          }
        })
      );
      this.unresolvedCommentCount$ = this.comments$.pipe(
        map(comments => comments.filter(comment => !comment.resolved).length)
      );
    }
  }

  async resolveThread(thread: CommentThread, resolved: boolean) {
    if (this.selectedThread()?.id === thread.id) {
      this.selectThread(undefined);
    }

    thread.resolvedTempOverride = thread.resolved;
    thread.resolved = resolved;
    await sleep(250);

    this.analytics.track(resolved ? 'comment_resolved' : 'comment_unresolved', false, {
      annotation: !!thread.annotation,
      text: !!thread.text,
    });

    this.commentService
      .updateComment(this.asset.id, thread.id, { resolved, text: thread.text }, this.gallery().id)
      .subscribe(() => {
        thread.resolvedTempOverride = undefined;
      });
  }

  replyFocusChanged(focused: boolean) {
    this.replyFocused.set(focused);
  }

  toggleSelectedThread(thread: CommentThread) {
    if (this.selectedThread()?.id === thread?.id) {
      return this.selectThread(undefined);
    } else {
      this.selectThread(thread);
    }
  }

  selectThread(thread: CommentThread): void {
    if (!thread && !this.selectedThread()) return;

    if (!thread) this.replyFocusChanged(false);

    this.selectedThread.set(thread);

    if (thread) {
      const threadEl = this.threads?.nativeElement.querySelector(`#${thread.id}`);
      if (threadEl) threadEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else this.threads?.nativeElement.parentElement.scrollTo({ top: 0, behavior: 'smooth' });

      if (thread.resolved && !this.showResolved) {
        this.toggleShowResolved.emit();
      }
    }

    this.selectedThreadChange.emit(thread?.id);
    if (thread && thread.annotation) {
      this.viewAnnotation.emit({
        commentId: thread.id,
        shapes: thread.annotation as AssetCommentAnnotationDto[] as Shape[],
        frame: thread.position?.frame?.[0] || thread.position?.frame,
        created_at: thread.created_at,
      });
    } else {
      this.viewAnnotation.emit(undefined);
      if (this.asset.type === 'video' && thread?.position?.frame !== undefined) {
        const fraction = Array.isArray(thread.position.frame) ? thread.position.frame[0] : thread.position.frame;
        this.videoTimeChange.emit(fraction);
      }

      if (thread?.position?.x) {
        this.zoomToComment.emit(thread);
      } else {
        this.zoomToComment.emit(undefined);
      }
    }
  }

  deleteComment(comment: Comment | CommentThread): void {
    // temp until the backend automatically deletes replies
    if ('replies' in comment) {
      comment.replies.forEach(reply => this.deleteComment(reply));
    }
    this.analytics.track('comment_deleted', false, {
      annotation: !!comment.annotation,
      text: !!comment.text,
      reply: !!comment.parent_comment,
    });
    this.commentService.deleteComment(this.asset.id, comment.id, this.gallery().id).subscribe();
  }

  postNewThread(text: string, addVideoFramePosition: boolean): void {
    this.feedbackService
      .sendComment(
        this.asset,
        this.gallery(),
        { text, private: this.commentMessage.private },
        this.createAnnotationShapes,
        addVideoFramePosition ? this.commentService.getCommentPosition(this.asset, this.videoTime) : undefined
      )
      .then(comment => {
        this.threads?.nativeElement.parentElement.scrollTo({ top: 0, behavior: 'smooth' });
        this.onNewThread.emit(comment as CommentThread);
        if (this.annotationMode) {
          this.toggleAnnotationMode.emit();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
